import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './ui/Header';
import { ProgressStepper } from './ui/ProgressStepper';
import { OPENFINANCE_THEME, getOpenFinanceLogo } from '../constants/openFinanceTheme';
export interface ConsentLayoutProps {
  children: React.ReactNode;
  currentStep?: 1 | 2 | 3;
  context: 'TPP' | 'LFI';
  onBack?: () => void;
  showProgress?: boolean;
  bankName?: string;
  bankLogoUrl?: string;
  bankColor?: string;
  tppLogoUrl?: string;
}
export function ConsentLayout({
  children,
  currentStep = 1,
  context,
  onBack,
  showProgress = true,
  bankName,
  bankLogoUrl,
  bankColor,
  tppLogoUrl
}: ConsentLayoutProps) {
  const steps = ['Consent', 'Authorize', 'Complete'];
  // Use OpenFinance-OS branding for TPP context
  const isTPP = context === 'TPP';
  const contextColor = isTPP ? OPENFINANCE_THEME.colors.primary500 : bankColor;
  const contextGradient = isTPP ? OPENFINANCE_THEME.gradients.primary : `linear-gradient(135deg, ${bankColor} 0%, ${bankColor}dd 100%)`;
  return <div className="min-h-screen flex items-center justify-center p-4" style={{
    background: isTPP ? OPENFINANCE_THEME.colors.background : '#F5F9FA',
    fontFamily: OPENFINANCE_THEME.fonts.body
  }} data-id="element-78">
      <div className="w-full max-w-md" data-id="element-79">
        {/* Context Indicator Badge with animation */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="flex justify-center mb-3" data-id="element-80">
          <motion.div key={context} initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-500 shadow-lg" style={{
          background: contextGradient,
          color: 'white',
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-81">
            <motion.div animate={{
            scale: [1, 1.2, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="w-2 h-2 rounded-full bg-white" data-id="element-82" />
            <span data-id="element-83">
              {isTPP ? 'OpenFinance-OS Platform' : `${bankName} Banking`}
            </span>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="bg-white overflow-hidden transition-all duration-500" style={{
        borderRadius: OPENFINANCE_THEME.radius.xl,
        boxShadow: OPENFINANCE_THEME.shadows.xl
      }} data-id="element-84">
          <Header title="Consent Journey" context={context} onBack={onBack} bankName={bankName} bankLogoUrl={bankLogoUrl} bankColor={bankColor} tppLogoUrl={tppLogoUrl} data-id="element-85" />

          {showProgress && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          delay: 0.2
        }} className="px-6 py-8 border-b" style={{
          borderColor: OPENFINANCE_THEME.colors.borderLight,
          background: isTPP ? OPENFINANCE_THEME.gradients.card : 'linear-gradient(to bottom, white, #F9FAFB)'
        }} data-id="element-86">
              <div className="flex flex-col items-center space-y-6" data-id="element-87">
                <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.3
            }} className="flex justify-center" data-id="element-88">
                  <img src="https://cdn.magicpatterns.com/uploads/fGMtEsLeHZt6m9wLuSFi4B/Dark_Logo.svg" alt="ALTAREQ" className="h-12 w-auto object-contain" data-id="element-89" />
                </motion.div>
                <ProgressStepper currentStep={currentStep} steps={steps} data-id="element-90" />
              </div>
            </motion.div>}

          <div className="p-6" data-id="element-91">
            <AnimatePresence mode="wait" data-id="element-92">{children}</AnimatePresence>
          </div>
        </motion.div>

        {/* Powered by OpenFinance-OS Footer */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3,
        delay: 0.4
      }} className="text-center mt-4 text-xs" style={{
        color: OPENFINANCE_THEME.colors.textMuted,
        fontFamily: OPENFINANCE_THEME.fonts.body
      }} data-id="element-93">
          {isTPP ? <>
              Secured by{' '}
              <span className="font-semibold" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-94">
                AlTareq Open Finance Platform
              </span>
            </> : <>
              Secured by{' '}
              <span className="font-semibold text-gray-700" data-id="element-95">
                AlTareq Open Finance Platform
              </span>
            </>}
        </motion.div>
      </div>
    </div>;
}